import { Body } from '@depop/web-thrift-ui/Typography';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { type DisplayPrice } from '@/modules/price/aliases';
import { getSizeString } from '@/modules/product/helpers/getSizeString';

const PRODUCT_ROUTE = /\/products\//;

type Props = {
  sizes: string[];
  brandName?: string;
  displayPrice: DisplayPrice;
};

export function ProductCardAttributes({
  sizes,
  brandName,
  displayPrice,
}: Props) {
  const t = useTranslations('common');
  const pathname = usePathname();
  const isProductPage = PRODUCT_ROUTE.test(pathname);
  const sizeString = getSizeString({
    sizes,
    multipleSizesString: t('MultipleSizes'),
  });

  const showBrandName = brandName ? (
    <Body variant="sm-regular" className={styles.brandName}>
      {brandName}
    </Body>
  ) : null;

  return (
    <div className={styles.attributesContainer}>
      {/* this conditional is needed to match the product page layout */}
      {isProductPage && showBrandName}
      {/* eslint-disable-next-line no-restricted-syntax */}
      <Body variant="sm-regular" data-testid="productCard__sizeAttr">
        {sizeString}
      </Body>
      <Price
        currencyCode={displayPrice.headline_price.currency as CurrencyCode}
        discountedPrice={
          displayPrice.strikethrough_price
            ? displayPrice.headline_price.amount
            : undefined
        }
        price={
          displayPrice.strikethrough_price?.amount ??
          displayPrice.headline_price.amount
        }
      />
      {displayPrice.secondary_price && (
        <SecondaryPriceLine secondaryPrice={displayPrice.secondary_price} />
      )}
      {!isProductPage && showBrandName}
    </div>
  );
}
